export interface LiveKitRoomIdentity {
  roomName: string
  colyseusRoomId: string
}

/** Trim for stable comparison; empty means "unknown until server sends full token payload". */
export function normalizeLiveKitColyseusRoomId(id: string | undefined | null): string {
  return (id ?? '').trim()
}

/**
 * True when the user should move to a different LiveKit session (different room name or genuinely different Colyseus room).
 *
 * When `colyseusRoomId` flips from empty to a real id for the same LiveKit `roomName`, that is **not** a new room — it avoids
 * disconnect storms from late-arriving LIVEKIT_TOKEN fields.
 */
export function shouldTreatAsNewRoom(
  previousRoomData: LiveKitRoomIdentity | null,
  nextRoomData: LiveKitRoomIdentity
): boolean {
  if (!previousRoomData) return true
  if (previousRoomData.roomName !== nextRoomData.roomName) return true

  const prev = normalizeLiveKitColyseusRoomId(previousRoomData.colyseusRoomId)
  const next = normalizeLiveKitColyseusRoomId(nextRoomData.colyseusRoomId)

  if (prev === next) return false
  /** One side still unknown → same logical session until both ids are non-empty and disagree. */
  if (prev === '' || next === '') return false

  return true
}

export function shouldUseCanonicalScreenShareSessionId(sessionId: string): boolean {
  return sessionId.trim().length > 0
}

/**
 * Whether a freshly published camera track already carries the processor we
 * parked for it (SC-158).
 *
 * Background effects are attached at **capture time** — the processor is put in
 * `videoCaptureDefaults`, and LiveKit awaits `setProcessor` inside
 * `createLocalTracks`, before the track is published. That is what stops the
 * user's real room being published and shown for the moment between publish and
 * attach.
 *
 * The consequence for the publish handler is that it must **adopt** what is
 * already attached rather than build a second processor: two would mean two
 * WebGL contexts for one camera, and the page has a hard ceiling on those.
 */
export function shouldAdoptCaptureTimeProcessor(
  attachedProcessor: unknown,
  parkedProcessor: unknown
): boolean {
  return Boolean(attachedProcessor) && attachedProcessor === parkedProcessor
}

/**
 * Whether a processor should be parked in `videoCaptureDefaults` for the next
 * camera start.
 *
 * Parking is cheap — constructing a processor allocates no WebGL context; that
 * happens in `init()`, which LiveKit calls when it attaches. So the only reasons
 * not to park are that there is no effect to apply, or that the device cannot
 * run one.
 */
export function shouldParkCaptureProcessor(
  hasActiveEffect: boolean,
  featureAvailable: boolean
): boolean {
  return hasActiveEffect && featureAvailable
}

/**
 * Whether a parked processor is still usable for the next camera start.
 *
 * LiveKit destroys a track's processor in `LocalTrack.stop()`, which runs when
 * a camera track is unpublished or replaced (a room switch, a held camera taken
 * down). Turning the camera off is a mute, not an unpublish: the track keeps its
 * processor and restarts it on unmute. A processor that has been attached once
 * is therefore spent for any *other* track: reusing it would hand LiveKit a
 * destroyed object and publish a raw track — the exact flash this avoids.
 *
 * `alreadyAttached` comes from the processor itself (`processedTrack` is set by
 * its first `init()`), not from our bookkeeping: LiveKit can start a parked
 * processor on a path whose publish we never see, such as a camera start that
 * failed after the track was created.
 */
export function isParkedProcessorReusable(
  parkedEffectKey: string | null,
  wantedEffectKey: string,
  alreadyAttached: boolean
): boolean {
  if (alreadyAttached) return false
  return parkedEffectKey === wantedEffectKey
}

/**
 * What a camera start that failed inside LiveKit shows (SC-158, F8).
 *
 * The background lifecycle is asked first. When it took the failure — the
 * effect would not start, or the start was given up on for a newer choice —
 * the camera is still wanted on, so it stays "on, waiting for background"
 * (amber) on the camera button and our own tile: what our nameplate shows and
 * everyone else is told, and what a start the gate kept off shows. Only a
 * failure it did not take, the device's, is shown as the camera off.
 */
export function showFailedCameraStart(
  backgroundTookFailure: () => boolean,
  showCameraOff: () => void
): void {
  if (!backgroundTookFailure()) showCameraOff()
}

/**
 * What to do once a camera on or off has settled, so the camera ends in the
 * state the user last chose (SC-158, QA case E10).
 *
 * A start can wait a long time inside LiveKit for its background processor,
 * and the user can turn the camera off and on again meanwhile. LiveKit parks
 * an off made then until the start publishes (giving up after 10 s), and the
 * start-in-progress guard turns away a new on. So the operation that settles
 * can disagree with the user's latest click:
 * - the choice is off and the camera is on: turn it off again;
 * - the choice is on and the camera is off: start it again, through the gate,
 *   unless a start is still running (its own settle decides) or the network
 *   is keeping video off.
 *
 * One answer per settle, and a start only when none is running, so it cannot
 * loop: each follow-up makes the camera agree with the choice.
 */
export function decideCameraFollowUp(state: {
  /** The user's last choice: `preferVideoEnabled`, written by the button before it toggles. */
  wantsOn: boolean
  /** The camera is published and unmuted. */
  cameraOn: boolean
  /** Another camera start is still running. */
  startRunning: boolean
  /** The network-quality policy has turned video off. */
  videoSuppressed: boolean
}): 'none' | 'turn-off' | 'start' {
  if (!state.wantsOn) return state.cameraOn ? 'turn-off' : 'none'
  if (state.cameraOn || state.startRunning || state.videoSuppressed) return 'none'
  return 'start'
}
